<template>
  <div class="timetable">
    <details
      ref="detailsRefs"
      v-for="(raceList, key, index) in raceListByStartDate"
      :key="`${isSingleDayChampionship}-${index}`"
      class="timetable__block"
      :open="isSingleDayChampionship || openStates[index]"
    >
      <!-- 開閉状態をVue側(openStates)で管理するため、summaryの開閉動作を防止する -->
      <summary @click.prevent="onSummaryClick(index)" class="timetable__summary">
        <timetable-header-parts
          :title="getDayNumber(key)"
          :date="key"
          :isSingleDayChampionship="isSingleDayChampionship"
        />
      </summary>

      <ol class="timetable__list">
        <li v-for="(item, index) in raceList" :key="index" class="timetable__item">
          <timetable-item-parts
            :raceDataItem="item"
            :contentsInfos="contentsInfos"
            :angleMovieInfos="angleMovieInfos"
            :qualifyingRaceStartEventList="
              item.raceType === 'QUALIFYING' ? qualifyingRaceStartEventList : undefined
            "
          />
        </li>
      </ol>
    </details>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  PropType,
  Ref,
  onMounted,
  nextTick,
} from '@vue/composition-api'
import StoreUtil from '@/store/StoreUtil'
import TimetableHeaderParts from '@/components/RaceListPage/RaceListPane/parts/TimetableHeaderParts.vue'
import TimetableItemParts from '@/components/RaceListPage/RaceListPane/parts/TimetableItemParts.vue'
import RaceDocument from '@/store/stores/collectionModule/documents/race/RaceDocument'
import ContentsInfoDocument from '@/store/stores/collectionModule/documents/contents/ContentsInfoDocument'
import AngleMovieInfoDocument from '@/store/stores/collectionModule/documents/angleMovie/AngleMovieInfoDocument'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'

export default defineComponent({
  name: 'RaceTimetableSection',
  components: { TimetableItemParts, TimetableHeaderParts },
  props: {
    /**
     * レース情報
     */
    raceListByStartDate: {
      type: Object as PropType<Record<string, Array<RaceDocument>>>,
    },
  },
  setup(props) {
    const topPageStore = StoreUtil.useStore('TopPageStore')

    const { contentsInfos, angleMovieInfos, qualifyingRaceStartEventList } = topPageStore

    const detailsRefs = ref<(HTMLDetailsElement | null)[]>([])

    /**
     * 開閉状態
     * デフォルトで開いた状態にするのはDay3まで対応できるようにする
     */
    const openStates = ref<boolean[]>(Array(3).fill(true))

    /**
     * 1日のみの大会かどうか
     */
    const isSingleDayChampionship = computed(
      () => Object.keys(props.raceListByStartDate || {}).length === 1,
    )

    /**
     * 開閉状態を制御するためのクリックイベント
     */
    const onSummaryClick = (index: number) => {
      if (!isSingleDayChampionship.value) {
        // 開閉を制御
        openStates.value[index] = !openStates.value[index]

        // DOMにも反映
        const detail = detailsRefs.value[index]
        if (detail) {
          detail.open = openStates.value[index]
        }
      }
    }

    /**
     * DAY1、DAY2などを取得
     */
    const getDayNumber = (dateKey: string) => {
      if (!props.raceListByStartDate) return ''

      const keysArr = Object.keys(props.raceListByStartDate).filter((key) => key !== '')
      const index = keysArr.indexOf(dateKey)
      return index === -1 ? '' : `DAY ${index + 1}`
    }

    onMounted(() => {
      nextTick(() => {
        // 初期表示の状態を反映
        detailsRefs.value.forEach((detail, i) => {
          if (detail) {
            // eslint-disable-next-line no-param-reassign
            detail.open = isSingleDayChampionship.value || openStates.value[i]
          }
        })
      })
    })

    return {
      detailsRefs,
      openStates,
      isSingleDayChampionship,
      onSummaryClick,
      getDayNumber,
      contentsInfos: contentsInfos as Ref<Array<ContentsInfoDocument>>,
      angleMovieInfos: angleMovieInfos as Ref<Array<AngleMovieInfoDocument>>,
      qualifyingRaceStartEventList: qualifyingRaceStartEventList as Ref<Array<HighlightDocument>>,
    }
  },
})
</script>

<style scoped lang="scss">
.timetable {
  text-align: left;

  &__block {
    + .timetable__block {
      margin-top: 33px;
    }

    &[open] {
      &::v-deep {
        .timetable-header__title-icon {
          transform: rotate(0);
        }
      }
    }
  }

  &__list {
    margin-top: 12px;
  }

  summary {
    list-style: none;
    outline: none;

    &::-webkit-details-marker {
      display: none;
    }
  }
}
</style>
